// frontend/src/components/SubmitWithBadgeDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
  TextField,
  Paper
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import SendIcon from '@mui/icons-material/Send';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PublishIcon from '@mui/icons-material/Publish';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Tooltip } from '@mui/material';
import { format, addDays } from 'date-fns';
import { getImageProductDetails } from '../api/images';

const SubmitWithBadgeDialog = ({ 
  open, 
  onClose, 
  selectedImages, 
  onSubmit,
  onScheduleBadgeRemovals,
  isSubmitting = false,
  onForceSubmit
}) => {
  const [badgeRemovalSuggestions, setBadgeRemovalSuggestions] = useState([]);
  const [selectedBadgeRemovals, setSelectedBadgeRemovals] = useState({});
  const [showBadgeSuggestions, setShowBadgeSuggestions] = useState(true);
  const [submitNow, setSubmitNow] = useState(true);
  const [scheduledDate, setScheduledDate] = useState('');
  const [customRemovalDates, setCustomRemovalDates] = useState({});
  const [manuallyEditedDates, setManuallyEditedDates] = useState(new Set());
  const [forceResubmit, setForceResubmit] = useState(false);
  const [publishOnSubmit, setPublishOnSubmit] = useState(true);
  const [productDetails, setProductDetails] = useState(null);
  const [productDetailsLoading, setProductDetailsLoading] = useState(false);
  const [productDetailsError, setProductDetailsError] = useState(null);
  // Editable copy of the Syndigo fields; changed values are pushed to Syndigo on submit.
  const [editedDetails, setEditedDetails] = useState({ title: '', description: '', feature_bullets: [], size: '', size_uom: '' });

  // Fetch Syndigo product details when a single image is selected
  useEffect(() => {
    if (!open || selectedImages.length !== 1) {
      setProductDetails(null);
      setProductDetailsError(null);
      return;
    }
    const imageId = selectedImages[0].id;
    let cancelled = false;
    setProductDetailsLoading(true);
    setProductDetailsError(null);
    getImageProductDetails(imageId)
      .then(data => {
        if (cancelled) return;
        setProductDetails(data);
        if (data?.found) {
          setEditedDetails({
            title: data.title || '',
            description: data.description || '',
            feature_bullets: Array.isArray(data.feature_bullets) ? [...data.feature_bullets] : [],
            size: data.size || '',
            size_uom: data.size_uom || '',
          });
        }
      })
      .catch(err => {
        if (!cancelled) setProductDetailsError(err?.detail || err?.message || 'Failed to fetch product details');
      })
      .finally(() => { if (!cancelled) setProductDetailsLoading(false); });
    return () => { cancelled = true; };
  }, [open, selectedImages]);

  // Initialize badge suggestions when dialog opens or images change
  useEffect(() => {
    if (open && selectedImages.length > 0) {
      console.log('SubmitWithBadgeDialog: Checking badges for', selectedImages.length, 'images');
      const suggestions = [];
      
      selectedImages.forEach(image => {
        console.log('Checking image:', image.id, 'GTIN:', image.gtin);
        console.log('product_data type:', typeof image.product_data);
        console.log('product_data value:', image.product_data);
        
        // Check for badges in product_badges field first (from list endpoint)
        let badges = [];
        if (image.product_badges && Array.isArray(image.product_badges)) {
          badges = image.product_badges;
          console.log('Found badges in product_badges field:', badges);
        } else if (image.product_data) {
          const productData = typeof image.product_data === 'string' 
            ? JSON.parse(image.product_data) 
            : image.product_data;
          
          console.log('Parsed product_data:', productData);
          console.log('Looking for badges in:', productData.badges, 'or final_badges:', productData.final_badges);
          
          badges = productData.badges || productData.final_badges || [];
          console.log('Found badges in product_data:', badges);
        }
        
        if (badges.length > 0) {
          console.log('Processing badges:', badges);

          badges.forEach(badge => {
            const isPermanent = !isTemporaryBadge(badge);
            console.log('Checking badge:', badge, 'isPermanent:', isPermanent);
            // Include ALL badges - permanent ones just won't be pre-selected
            const duration = getBadgeDuration(badge);
            suggestions.push({
              imageId: image.id,
              gtin: image.gtin,
              badge: badge,
              displayName: getBadgeDisplayName(badge),
              duration: duration,
              isPermanent: isPermanent
            });
          });
        } else {
          console.log('No product_data for image:', image.id);
        }
      });
      
      console.log('Total badge removal suggestions:', suggestions.length);
      setBadgeRemovalSuggestions(suggestions);
      
      // Pre-select only temporary badge removals by default (permanent badges start unchecked)
      // But preserve any existing selections from previous dialog opens
      setSelectedBadgeRemovals(prev => {
        const newSelections = { ...prev };
        suggestions.forEach(suggestion => {
          const key = `${suggestion.imageId}-${suggestion.badge}`;
          // Only set default if not already in state
          if (newSelections[key] === undefined) {
            newSelections[key] = !suggestion.isPermanent;
          }
        });
        return newSelections;
      });

      // Initialize custom removal dates - preserve existing edits, add from saved data
      const newManuallyEdited = new Set();
      const newCustomDates = {};

      // First, load any saved dates from the images
      selectedImages.forEach(image => {
        if (image.badge_removal_dates) {
          Object.entries(image.badge_removal_dates).forEach(([badge, date]) => {
            if (date) {
              const key = `${image.id}-${badge}`;
              newCustomDates[key] = date;
              newManuallyEdited.add(key);
            }
          });
        }
      });

      // Preserve any existing custom dates from current session (merge, don't replace)
      setCustomRemovalDates(prev => ({ ...newCustomDates, ...prev }));
      setManuallyEditedDates(prev => new Set([...newManuallyEdited, ...prev]));
    }
  }, [open, selectedImages]);

  // Calculate suggested removal dates whenever submit timing changes
  const getCalculatedRemovalDate = (suggestion) => {
    const baseDate = (!submitNow && scheduledDate) ? new Date(scheduledDate) : new Date();
    return addDays(baseDate, suggestion.duration);
  };

  const isTemporaryBadge = (badge) => {
    // Uses exact PascalCase badge names as stored in the database
    const permanentBadges = [
      'Organic', 'GlutenFree', 'NonGMO', 'SugarFree', 'Keto',
      'PlantBased', 'Vegan', 'FlavorMeter', '10DayGuarantee',
      '10DayFreshGuarantee', 'FSA_HSA', 'OptUp'
    ];
    return !permanentBadges.includes(badge);
  };

  const getBadgeDuration = (badge) => {
    // Uses exact PascalCase badge names as stored in the database
    const durations = {
      'NewItem': 84,  // 12 weeks
      'NewLook': 84,  // 12 weeks
      'NewSize': 84,  // 12 weeks
      'NewFlavor': 84,  // 12 weeks
      'NewDesign': 84,  // 12 weeks
      'NewImproved': 84,  // 12 weeks
      'LimitedTime': 30,
      'LimitedEdition': 60,
      'SaveNow': 14,
      'SpecialOffer': 30,
      'Seasonal': 90,
      'Holiday': 45,
      'AwardWinning': 180,  // 6 months
      'BestSeller': 120,
      'Exclusive': 120,
      'OnlyAtKroger': 180,
      // Permanent badges - use longer default if user chooses to schedule removal
      'Organic': 365,
      'GlutenFree': 365,
      'NonGMO': 365,
      'SugarFree': 365,
      'Keto': 365,
      'PlantBased': 365,
      'Vegan': 365,
      'FlavorMeter': 365,
      '10DayGuarantee': 365,
      '10DayFreshGuarantee': 365,
      'FSA_HSA': 365,
      'OptUp': 365
    };
    return durations[badge] || 84; // Default to 12 weeks (84 days)
  };

  const getBadgeDisplayName = (badge) => {
    // Uses exact PascalCase badge names as stored in the database
    const names = {
      'NewItem': 'New Item',
      'NewLook': 'New Look',
      'NewSize': 'New Size',
      'NewFlavor': 'New Flavor',
      'NewDesign': 'New Design',
      'NewImproved': 'New & Improved',
      'LimitedTime': 'Limited Time',
      'LimitedEdition': 'Limited Edition',
      'SaveNow': 'Save Now',
      'SpecialOffer': 'Special Offer',
      'Seasonal': 'Seasonal',
      'Holiday': 'Holiday',
      'AwardWinning': 'Award Winning',
      'BestSeller': 'Best Seller',
      'Exclusive': 'Exclusive',
      'OnlyAtKroger': 'Only at Kroger',
      'Organic': 'Organic',
      'GlutenFree': 'Gluten Free',
      'NonGMO': 'Non-GMO',
      'SugarFree': 'Sugar Free',
      'Keto': 'Keto Friendly',
      'PlantBased': 'Plant Based',
      'Vegan': 'Vegan',
      'FlavorMeter': 'Flavor Meter',
      '10DayGuarantee': '10 Day Fresh Guarantee',
      '10DayFreshGuarantee': '10 Day Fresh Guarantee',
      'FSA_HSA': 'FSA/HSA Eligible',
      'OptUp': 'Opt Up'
    };
    return names[badge] || badge;
  };

  // Which Syndigo fields the user changed (single-image submit only). Returns
  // an object of only-changed attrs, or null if nothing changed.
  const getChangedAttributes = () => {
    if (selectedImages.length !== 1 || !productDetails?.found) return null;
    const changes = {};
    if (editedDetails.title !== (productDetails.title || '')) changes.title = editedDetails.title;
    if (editedDetails.description !== (productDetails.description || '')) changes.description = editedDetails.description;
    if (editedDetails.size !== (productDetails.size || '')) changes.size = editedDetails.size;
    const origBullets = (Array.isArray(productDetails.feature_bullets) ? productDetails.feature_bullets : []).map(x => (x || '').trim());
    const newBullets = editedDetails.feature_bullets.map(x => (x || '').trim()).filter(x => x !== '');
    if (JSON.stringify(newBullets) !== JSON.stringify(origBullets)) changes.feature_bullets = newBullets;
    return Object.keys(changes).length > 0 ? changes : null;
  };

  const handleSubmit = async (publish) => {
    // Prepare badge removals first
    const selectedRemovals = Object.entries(selectedBadgeRemovals)
      .filter(([key, selected]) => selected)
      .map(([key]) => {
        const suggestion = badgeRemovalSuggestions.find(s => `${s.imageId}-${s.badge}` === key);
        if (suggestion) {
          // Use custom date if manually edited, otherwise use calculated date
          const isManuallyEdited = manuallyEditedDates.has(key);
          const customDate = customRemovalDates[key];
          const calculatedDate = getCalculatedRemovalDate(suggestion);

          return {
            ...suggestion,
            removalDate: (isManuallyEdited && customDate !== undefined && customDate !== '')
              ? new Date(customDate)
              : calculatedDate
          };
        }
        return null;
      })
      .filter(Boolean);

    // For immediate submissions, schedule badge removals BEFORE submitting
    // because the image needs to be staged to schedule removals
    if (submitNow && selectedRemovals.length > 0 && onScheduleBadgeRemovals) {
      console.log('Scheduling badge removals before immediate submission');
      await onScheduleBadgeRemovals(selectedRemovals);
    }

    // Now submit the images
    if (submitNow) {
      // Submit immediately
      const productAttributeUpdates = getChangedAttributes();
      if (forceResubmit && onForceSubmit) {
        // Use force submit endpoint
        await onForceSubmit({ publishAfterSubmit: !!publish, productAttributeUpdates });
      } else {
        // Normal submit
        await onSubmit(null, { publishAfterSubmit: !!publish, productAttributeUpdates });
      }
    } else {
      // Schedule the submission (force submit doesn't apply to scheduled)
      const result = await onSubmit(scheduledDate, { publishOnSubmit: !!publish });

      // For scheduled submissions, we can schedule badge removals after
      // because the image remains staged
      // result may be a single submission (EditImagePage) or array (EnhancedReviewSubmitPage)
      if (selectedRemovals.length > 0 && onScheduleBadgeRemovals && result) {
        const parentSubmissions = Array.isArray(result) ? result : [result];

        for (const parentSubmission of parentSubmissions) {
          // Filter removals to only those for this parent's image
          const removalsForImage = selectedRemovals.filter(
            r => r.imageId === parentSubmission.image_id
          );
          if (removalsForImage.length > 0) {
            console.log('Scheduling badge removals for parent submission', parentSubmission.id, 'image', parentSubmission.image_id);
            await onScheduleBadgeRemovals(removalsForImage, parentSubmission.id);
          }
        }
      }
    }
  };

  const toggleBadgeRemoval = (imageId, badge) => {
    const key = `${imageId}-${badge}`;
    setSelectedBadgeRemovals(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleDateChange = (key, value) => {
    setCustomRemovalDates(prev => ({
      ...prev,
      [key]: value
    }));
    // Mark this date as manually edited
    setManuallyEditedDates(prev => new Set(prev).add(key));
  };

  const selectedRemovalCount = Object.values(selectedBadgeRemovals).filter(Boolean).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SendIcon color="primary" />
          Confirm Submission to Syndigo
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography>
            You are about to submit {selectedImages.length} image{selectedImages.length !== 1 ? 's' : ''} to Syndigo.
          </Typography>

          {/* Product details from Syndigo - only shown when reviewing a single MRHI */}
          {selectedImages.length === 1 && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" gutterBottom>
                Current Syndigo data for GTIN {selectedImages[0].gtin}
              </Typography>
              {productDetailsLoading && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Loading product details…</Typography>
                </Box>
              )}
              {productDetailsError && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Couldn't load product details: {productDetailsError}
                </Alert>
              )}
              {!productDetailsLoading && !productDetailsError && productDetails && !productDetails.found && (
                <Typography variant="body2" color="text.secondary">No product found in Syndigo for this GTIN.</Typography>
              )}
              {!productDetailsLoading && !productDetailsError && productDetails?.found && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                    Edits below are pushed to Syndigo when you submit.
                  </Typography>
                  <TextField
                    label="Title" fullWidth size="small" sx={{ mb: 1.5 }}
                    value={editedDetails.title}
                    onChange={(e) => setEditedDetails(d => ({ ...d, title: e.target.value }))}
                    disabled={isSubmitting}
                  />
                  <TextField
                    label="Description" fullWidth size="small" multiline minRows={2} sx={{ mb: 1.5 }}
                    value={editedDetails.description}
                    onChange={(e) => setEditedDetails(d => ({ ...d, description: e.target.value }))}
                    disabled={isSubmitting}
                  />
                  <TextField
                    label="Size" size="small" sx={{ mb: 1.5, width: 240 }}
                    value={editedDetails.size}
                    onChange={(e) => setEditedDetails(d => ({ ...d, size: e.target.value }))}
                    disabled={isSubmitting}
                    helperText={editedDetails.size_uom ? `UOM: ${editedDetails.size_uom}` : ''}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Feature bullets</Typography>
                  {editedDetails.feature_bullets.map((b, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <TextField
                        fullWidth size="small"
                        value={b}
                        onChange={(e) => setEditedDetails(d => {
                          const fb = [...d.feature_bullets]; fb[i] = e.target.value; return { ...d, feature_bullets: fb };
                        })}
                        disabled={isSubmitting}
                      />
                      <IconButton size="small" disabled={isSubmitting} aria-label="remove bullet"
                        onClick={() => setEditedDetails(d => ({ ...d, feature_bullets: d.feature_bullets.filter((_, j) => j !== i) }))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  {editedDetails.feature_bullets.length < 7 && (
                    <Button size="small" startIcon={<AddIcon />} disabled={isSubmitting}
                      onClick={() => setEditedDetails(d => ({ ...d, feature_bullets: [...d.feature_bullets, ''] }))}
                    >
                      Add bullet
                    </Button>
                  )}
                </Box>
              )}
            </Paper>
          )}

          {/* Submit timing section */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Submit Timing</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={submitNow}
                  onChange={(e) => {
                    setSubmitNow(e.target.checked);
                    if (!e.target.checked && !scheduledDate) {
                      // Set default to tomorrow if switching to scheduled
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      setScheduledDate(format(tomorrow, 'yyyy-MM-dd\'T\'HH:mm'));
                    } else if (e.target.checked) {
                      // Clear scheduled date when switching back to immediate
                      setScheduledDate('');
                    }
                  }}
                />
              }
              label="Submit immediately"
            />
            {!submitNow && (
              <TextField
                type="datetime-local"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                sx={{ ml: 4, mt: 1 }}
                size="small"
                label="Schedule for"
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Box>

          {/* Publish toggle - shown only for the scheduled path. For immediate submit
              the user picks Submit Only vs Submit & Publish via the dialog buttons. */}
          {!submitNow && (
            <Box sx={{ mt: 2, mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={publishOnSubmit}
                    onChange={(e) => setPublishOnSubmit(e.target.checked)}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>Publish to recipient when scheduled submit fires</Typography>
                    <Tooltip
                      title="When the scheduled submission runs, also call Syndigo's publish-to-recipient API so the product goes live without a manual step."
                      placement="top"
                      arrow
                    >
                      <InfoIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                }
              />
            </Box>
          )}

          {/* Force Resubmit option - only show for already submitted images */}
          {selectedImages.some(img => img.is_submitted) && (
            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={forceResubmit}
                    onChange={(e) => setForceResubmit(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>Force Resubmit</Typography>
                    <Tooltip 
                      title="Only use this option if your resubmissions are not appearing in Syndigo. This will bypass duplicate prevention and always upload a new image."
                      placement="top"
                      arrow
                    >
                      <InfoIcon sx={{ fontSize: 18, color: 'warning.dark' }} />
                    </Tooltip>
                  </Box>
                }
              />
              {forceResubmit && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Force resubmit will upload the image even if it already exists in Syndigo. 
                    Use only when necessary to avoid creating duplicates.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {badgeRemovalSuggestions.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showBadgeSuggestions}
                      onChange={(e) => setShowBadgeSuggestions(e.target.checked)}
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography>Schedule automatic badge removals</Typography>
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                  Temporary badges are pre-selected for removal. Permanent badges can also be scheduled if needed.
                  {selectedRemovalCount > 0 && ` (${selectedRemovalCount} selected)`}
                </Typography>
              </Box>

              {showBadgeSuggestions && (
                <Box sx={{ maxHeight: 300, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  <List dense>
                    {badgeRemovalSuggestions.map((suggestion, index) => {
                      const key = `${suggestion.imageId}-${suggestion.badge}`;
                      const isManuallyEdited = manuallyEditedDates.has(key);
                      const displayDate = (isManuallyEdited && customRemovalDates[key] !== undefined && customRemovalDates[key] !== '')
                        ? customRemovalDates[key]
                        : format(getCalculatedRemovalDate(suggestion), 'yyyy-MM-dd');
                      
                      return (
                        <ListItem key={index} divider={index < badgeRemovalSuggestions.length - 1}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedBadgeRemovals[key] || false}
                                onChange={() => toggleBadgeRemoval(suggestion.imageId, suggestion.badge)}
                                size="small"
                              />
                            }
                            label=""
                          />
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2">{suggestion.gtin}</Typography>
                                <Chip
                                  label={suggestion.displayName}
                                  size="small"
                                  color={suggestion.isPermanent ? "default" : "primary"}
                                  variant="outlined"
                                />
                                {suggestion.isPermanent && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    (typically permanent)
                                  </Typography>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="caption" color="text.secondary">
                                  Remove on:
                                </Typography>
                                <TextField
                                  type="date"
                                  value={displayDate}
                                  onChange={(e) => handleDateChange(key, e.target.value)}
                                  size="small"
                                  disabled={!selectedBadgeRemovals[key]}
                                  sx={{ 
                                    '& .MuiInputBase-input': { 
                                      fontSize: '0.75rem',
                                      padding: '2px 8px'
                                    }
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  ({suggestion.duration} days {!submitNow && scheduledDate ? 'from scheduled date' : 'from today'})
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        {/* Left side: Submit & Publish — deliberately separated from the other
            actions so it can't be hit by accident. */}
        <Box>
          {submitNow && (
            <Button
              onClick={() => handleSubmit(true)}
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <PublishIcon />}
            >
              {isSubmitting ? 'Processing...' : 'Submit & Publish'}
            </Button>
          )}
        </Box>
        {/* Right side: Cancel + Submit Only (or Schedule) */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {submitNow ? (
            <Button
              onClick={() => handleSubmit(false)}
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            >
              {isSubmitting ? 'Processing...' : 'Submit Only'}
            </Button>
          ) : (
            <Button
              onClick={() => handleSubmit(publishOnSubmit)}
              variant="contained"
              disabled={isSubmitting || !scheduledDate}
              startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <ScheduleIcon />}
            >
              {isSubmitting
                ? 'Processing...'
                : (publishOnSubmit ? 'Schedule Submit & Publish' : 'Schedule Submission')}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitWithBadgeDialog;